/**
 * ADR-0002's second half: the stream is a convenience, so losing it must not turn the wall into a lie. Two
 * consecutive connection failures — or a browser that has stopped retrying at all — switch a page to an
 * explicit 15 s poll and the shell to a visible "degraded" state, instead of leaving a frozen chart looking like
 * a quiet plant.
 *
 * The machine is pure and separate from `useLiveStream` because the interesting part is not React but the
 * ordering: one blip must stay silent (the browser's own `retry: 3000` will fix it), a sustained loss must be
 * announced, and a successful reopen must clean up after itself.
 */
export const ERRORS_BEFORE_POLL = 2;
export const POLL_INTERVAL_MS = 15_000;

export type StreamPhase = 'connecting' | 'open' | 'degraded' | 'closed';

export interface StreamResilience {
  phase: StreamPhase;
  /** Consecutive failures since the last successful open. */
  errors: number;
}

export const initialStream = (): StreamResilience => ({ phase: 'connecting', errors: 0 });

export const onOpened = (state: StreamResilience): StreamResilience =>
  state.phase === 'open' && state.errors === 0 ? state : { phase: 'open', errors: 0 };

/**
 * `reconnecting` is `EventSource.readyState === CONNECTING`: the browser is still retrying on its own, so a
 * single failure is a blip and the UI should not flap. Once it has stopped retrying there is nothing left to
 * wait for, so polling starts on the first failure rather than the second.
 */
export const onFailed = (state: StreamResilience, reconnecting: boolean): StreamResilience => {
  const errors = state.errors + 1;
  if (!reconnecting) return { phase: 'degraded', errors };
  return { phase: errors >= ERRORS_BEFORE_POLL ? 'degraded' : 'connecting', errors };
};

export const shouldPoll = (state: StreamResilience): boolean => state.phase === 'degraded';
